import { useEffect, useState } from "react";

export interface UseMobileDetectionResult {
  isMobile: boolean;
  isMobileViewport: boolean;
  deviceType: "mobile" | "tablet" | "desktop";
}

/**
 * Hook to detect mobile devices and mobile viewport sizes
 * Uses multiple detection methods for better accuracy
 */
export const useMobileDetection = (): UseMobileDetectionResult => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(false);
  const [deviceType, setDeviceType] = useState<"mobile" | "tablet" | "desktop">(
    "desktop"
  );

  useEffect(() => {
    const detectMobile = () => {
      // Method 1: User Agent detection
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent);

      // Method 2: Touch capability detection
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

      // Method 3: Screen size detection
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isSmallScreen = screenWidth <= 768 || screenHeight <= 768;

      // Method 4: CSS media query detection
      const isMobileViewportSize =
        window.matchMedia("(max-width: 768px)").matches;

      // Method 5: Device orientation API (if available)
      const isPortrait = screenHeight > screenWidth;

      // Determine device type
      let detectedDeviceType: "mobile" | "tablet" | "desktop" = "desktop";

      if (isMobileDevice) {
        // Check if it's a tablet based on screen size
        if (screenWidth >= 768 && screenWidth <= 1024) {
          detectedDeviceType = "tablet";
        } else {
          detectedDeviceType = "mobile";
        }
      } else if (isSmallScreen || isMobileViewportSize) {
        // Desktop browser in mobile viewport
        detectedDeviceType = "mobile";
      }

      // Final mobile detection (combine multiple methods)
      const finalIsMobile =
        isMobileDevice || (hasTouch && isSmallScreen) || isMobileViewportSize;

      setIsMobile(finalIsMobile);
      setIsMobileViewport(isMobileViewportSize);
      setDeviceType(detectedDeviceType);
    };

    // Initial detection
    detectMobile();

    // Listen for resize events to detect viewport changes
    const handleResize = () => {
      detectMobile();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Listen for media query changes
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      setIsMobileViewport(e.matches);
      detectMobile();
    };

    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
    };
  }, []);

  return {
    isMobile,
    isMobileViewport,
    deviceType,
  };
};
